import React, { useState } from 'react';

import ClockIcon from 'icons/clock.svg';
import HhqLogo from 'icons/hhq.svg';
import DisableIcon from 'icons/ic-disable.svg';
import { useTranslation } from 'react-i18next';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import { useProjectActivityContext } from 'components/contexts/ProjectActivityContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import DisableLinkModal from 'components/projectActivity/components/DisableLinkModal';
import DispatchRow from 'components/projectActivity/components/DispatchRow';

import { asFormattedDateTime } from 'components/helpers/dates';
import { escapeDoubleQuotes } from 'components/helpers/strings';

const determineMessageStatus = (currentMessage) => {
  if (currentMessage.attributes['expired?']) {
    return 'expired';
  } else if (currentMessage.attributes.disabled) {
    return 'disabled';
  } else {
    return 'active';
  }
};

export default function ProjectMessageRow({ history, message }) {
  const { addInedibleToast } = useBreadBoard();
  const { isVersionPdfsEnabled } = useProjectActivityContext();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [messageStatus, setMessageStatus] = useState(
    determineMessageStatus(message.message),
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectActivity.components.ProjectMessageRow',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const handleDisableLink = () => {
    axios
      .patch(`/messages/${history.messageId}`, { disabled: true })
      .then((response) => {
        setMessageStatus(determineMessageStatus(response.data.data));
      })
      .catch(addInedibleToast);
  };

  const reason = escapeDoubleQuotes(history.reason);

  return (
    <>
      <tr className='handshq__tr--top-aligned hover:tw-bg-grey-025'>
        {isVersionPdfsEnabled && (
          <td className='tw-border-grey-100 tw-font-medium tw-text-grey-900'>
            {history.displayNumber}
          </td>
        )}

        <td className='tw-border-grey-100'>
          <div className='handshq__cell-content--near-spaced'>
            <ClockIcon
              className='tw-mr-1 [&_path]:tw-fill-grey-500'
              height={20}
              width={20}
            />
            <span>{asFormattedDateTime({ date: history.createdAt })}</span>
          </div>
        </td>

        <td className='tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-border-grey-100'>
          <div className='tw-mr-2'>
            {history.isApiActivity ?
              <div className='tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-mint-050'>
                <span className='tw-text-sm tw-flex tw-items-center tw-justify-center tw-align-middle tw-font-semibold tw-uppercase tw-text-mint-700'>
                  {i18nTCommon('shared.api')}
                </span>
              </div>
            : history.user && !history.nadminUser ?
              <ProfilePhotoDisplay
                componentSize='sm'
                photoUrl={history.profilePhoto?.croppedUrl}
                profileInitials={`${history.user.firstName[0]}${history.user.lastName[0]}`}
              />
            : history.nadminUser && <HhqLogo />}
          </div>
          <div className='tw-mb-1'>
            {history.nadminUser ?
              i18nTCommon('projects.nadminUserOnBehalfOf', {
                interpolation: { escapeValue: false },
                nadminUser: history.nadminUser,
                reason,
              })
            : reason}
          </div>
          {message?.dispatches.map((dispatch) => (
            <DispatchRow dispatch={dispatch} key={dispatch.id} />
          ))}
        </td>
        <td className='handshq__td-button-cell--align-right tw-border-grey-100'>
          {messageStatus &&
            (messageStatus === 'active' ?
              <OutlinedButton
                color='grey'
                onClick={() => setIsModalOpen(true)}
                size='sm'
              >
                <DisableIcon
                  className='-tw-ml-1.5 tw-mr-1'
                  height={20}
                  width={20}
                />
                <span>{i18nTCommon('shared.disable')}</span>
              </OutlinedButton>
            : <div>{i18nT(messageStatus)}</div>)}
        </td>
      </tr>
      {isModalOpen && (
        <DisableLinkModal
          onClose={() => setIsModalOpen(false)}
          onSubmit={() => {
            setIsModalOpen(false);
            handleDisableLink();
          }}
        />
      )}
    </>
  );
}
